import * as React from 'react';

import {
  markedPoints,
  viewBox,
  withinBox,
  type ExplorerPoint,
  type ViewBox,
} from './explorerPoints';

/**
 * What window of the flavor space the map is showing.
 *
 * Two problems live here, and they are the same problem.
 *
 * nivo defaults both axes to `min: 0` while UMAP coordinates are routinely
 * negative, so a domain has to be supplied or most of the space is drawn
 * outside the plot. And its canvas renderer paints every node it is handed —
 * `renderNode` is an `arc` with no `clip()` — so once a domain *is* supplied,
 * points outside it land on the axes and the margin unless they are filtered
 * out first.
 *
 * Fitting to the set rather than the universe is what the filtering buys: a
 * norm set occupies a fraction of a percent of the whole space, so drawn at
 * full extent it is a few pixels under sixteen thousand other points.
 */

export type MapView = {
  /** points inside the window, safe to hand to the renderer */
  visible: ExplorerPoint[];
  box: ViewBox | null;
  /** points left out, reported rather than silently dropped */
  offscreen: number;
  /** the anchors and members a focused view fits to */
  marked: ExplorerPoint[];
  /** false when there is nothing marked to fit to */
  canFocus: boolean;
  wholeSpace: boolean;
  setWholeSpace: (next: boolean) => void;
};

const useMapView = (points: ExplorerPoint[], focusOnSet: boolean): MapView => {
  const [wholeSpace, setWholeSpace] = React.useState(!focusOnSet);

  const marked = React.useMemo(() => markedPoints(points), [points]);
  const canFocus = marked.length > 0;

  const box = React.useMemo(
    () => viewBox(wholeSpace || !canFocus ? points : marked),
    [wholeSpace, canFocus, points, marked],
  );

  const { visible, offscreen } = React.useMemo(
    () => withinBox(points, box),
    [points, box],
  );

  return {
    visible,
    box,
    offscreen,
    marked,
    canFocus,
    wholeSpace,
    setWholeSpace,
  };
};

export default useMapView;
